import { installTranslateDomGuard } from './utils/translate-dom-guard';
import { createApp, nextTick } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
// Vuex store removed - fully migrated to Pinia
import vuetify from './plugins/vuetify'; // Vuetify 3 plugin
import VueTelInput from 'vue-tel-input';
import './styles/tokens.css';
import './styles/grid.styl';
import './styles/menu.css';
import 'vue-tel-input/vue-tel-input.css';
import 'driver.js/dist/driver.css';
import './tour/theme/asu-tour.css';
import AuthPlugin from './plugins/auth';
import ConnServicePlugin from './plugins/connection';
import SessionStoragePlugin from './plugins/session-storage'; // Vue 3 compatible session storage
import EventBusPlugin from './plugins/event-bus'; // Event bus for component communication
import dataLayer from './dataLayerConfig';
import { globalComponents } from './config/setup-components'; // Import global components
import { storeParamsFromUrl, ATTRIBUTION_PARAMS } from './util/param-persistence';
import { initSentry } from './services/sentry.service';
import { guardedChunkReload } from './utils/async-component-retry';

// Make Vue's DOM patching resilient to in-browser page translation (eaulc-web-app#463).
// Must run before createApp()/mount() so the patched prototypes are live for the first render.
installTranslateDomGuard();

// Load GTM
dataLayer(window, document, 'script', 'dataLayer', 'GTM-KDWN8Z');

// Capture campaign/partner attribution tags from the entry URL, regardless of landing route
storeParamsFromUrl(ATTRIBUTION_PARAMS);

// Create Vue 3 app instance
const app = createApp(App);

initSentry(app, router);

// A cached pre-deploy page can reference hashed chunks a later deploy removed;
// the import then gets the SPA's index.html fallback and rejects. Reload once
// (guarded by a query param) to fetch fresh HTML with current chunk URLs. When
// the guard says this page load already is the retry, let the error propagate
// so per-callsite handlers and Sentry see it instead of looping.
window.addEventListener('vite:preloadError', (event) => {
  if (guardedChunkReload({ trigger: 'vite_preload', error: String(event.payload) })) {
    event.preventDefault();
  }
});

// Register global components
globalComponents.forEach(({ name, component }) => {
  app.component(name, component);
});

// Create Pinia instance
const pinia = createPinia();

// Register plugins with the Vue 3 app instance
app.use(router);
app.use(pinia);

// Vuex to Pinia migration completed
app.use(vuetify); // Vuetify 3 - now compatible with Vue 3
// app.use(Vuesax); // TEMPORARILY DISABLED - Vue 2 only plugin
app.use(AuthPlugin);
app.use(ConnServicePlugin);
app.use(SessionStoragePlugin); // Vue 3 compatible session storage
app.use(EventBusPlugin); // Event bus for component communication
app.use(VueTelInput); // Vue 3 compatible telephone input plugin

// Mount the app and ensure ASU header initializes after Vue renders
app.mount('#app');

// Fix ASU header timing issue - ensure it runs after Vue DOM is ready
nextTick(() => {
  // Re-initialize ASU header after Vue has rendered
  if (window.ASUHeader && typeof window.ASUHeader.init === 'function') {
    window.ASUHeader.init();
  } else if (window.ASUHeader && typeof window.ASUHeader.toggleASU === 'function') {
    // Some versions might not have init, but have the toggle function
    // This ensures the header is properly initialized
    try {
      // Force re-evaluation of ASU header elements
      const asuHeaderElements = document.querySelectorAll('#asu_hdr, #asu_nav_wrapper, #asu_mobile_button');
      if (asuHeaderElements.length > 0) {
        // ASU Header elements found, header should be working
      }
    } catch (error) {
      // ASU Header initialization warning
    }
  }
});
